/* eslint-disable react/display-name, react/no-children-prop */
import { Fragment, createElement, forwardRef } from "react"
import type { ComponentType, ForwardRefExoticComponent, ForwardedRef, ReactElement, RefAttributes } from "react"

import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isNull from "lodash-es/isNull"
import { useSearchParams } from "react-router-dom"

import { DynamicHeightContainer, InfoBox } from "@/3514/components"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { coachingChatUiConfig as uiConfig } from "../config"
import { useCoachingChatContext as useContext } from "../context"
import { useCoachingChatConversationStatusSelector as useStatusSelector } from "../store"
import {
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode
} from "../types"

const { CHAT_AUTOSCROLL_SEARCH_PARAM_NAME: SEARCH_PARAM_NAME } = uiConfig

function withModeGuard<T, P>(
    Container: ComponentType<T & RefAttributes<P>>
): ForwardRefExoticComponent<RefAttributes<HTMLDivElement>> {
    return forwardRef<HTMLDivElement, void>((_: void, ref: ForwardedRef<HTMLDivElement>): ReactElement => {
        const { t }: IUseTranslation = useTranslation()

        const { entityMode }: ReturnType<typeof useContext> = useContext()

        const { entityId }: ReturnType<typeof useContext> = useContext()

        const status: EConversationStatus = useStatusSelector(entityId)

        return eq(entityMode, EEntityMode.Light)
            ? includes<EConversationStatus>(
                  [
                      EConversationStatus.Ready,
                      EConversationStatus.InProgress,
                      EConversationStatus.Paused,
                      EConversationStatus.GeneratingSummary,
                      EConversationStatus.Completed
                  ],
                  status
              )
                ? createElement(Container, { ref } as T)
                : includes<EConversationStatus>([EConversationStatus.Locked, EConversationStatus.ReadyToStart], status)
                  ? createElement(InfoBox, {
                        variant: "warning",
                        title: t("entities.coachingChat.chat.warning.unstarted.title"),
                        message: t("entities.coachingChat.chat.warning.unstarted.description")
                    })
                  : createElement(Fragment)
            : createElement(Container, { ref } as T)
    })
}

function withStatusGuard<T, P>(
    Container: ComponentType<T & RefAttributes<P>>
): ForwardRefExoticComponent<RefAttributes<HTMLDivElement>> {
    return forwardRef<HTMLDivElement, void>((_: void, ref: ForwardedRef<HTMLDivElement>): ReactElement => {
        const { entityId }: ReturnType<typeof useContext> = useContext()

        const status: EConversationStatus = useStatusSelector(entityId)

        return eq(status, EConversationStatus.NotAvailable)
            ? createElement(Fragment)
            : createElement(Container, { ref } as T)
    })
}

function withDynamicHeightGuard<T, P>(
    Container: ComponentType<T & RefAttributes<P>>
): ForwardRefExoticComponent<RefAttributes<HTMLDivElement>> {
    return forwardRef<HTMLDivElement, void>((_: void, ref: ForwardedRef<HTMLDivElement>): ReactElement => {
        const [searchParams]: ReturnType<typeof useSearchParams> = useSearchParams()

        return isNull(searchParams.get(SEARCH_PARAM_NAME))
            ? createElement(Container, { ref } as T)
            : createElement(DynamicHeightContainer, {
                  timeoutInMs: 300,
                  children: createElement(Container, { ref } as T)
              })
    })
}

const guards = {
    withStatusGuard,
    withModeGuard,
    withDynamicHeightGuard
}

export { guards as coachingChatConversationContainerGuards }
